import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useForm, useField } from 'react-final-form-hooks';
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { useTheme } from '../../ThemeContext';

import { signInInit, signIn } from '../../actions/Auth/AuthActions';

import IMAGE_LOGO from '../../assets/Images/Logo.svg';

import {
  Panel,
  Logobox,
  Logo,
  Textbox,
  Text,
  Strong,
  Form,
  Inputbox,
  Input,
  InputError,
  Checkbox,
  Checkmark,
  Check,
  SubmitButton,
  Linkbox,
} from './SignInPanel.style';

const SignInPanel = () => {
  const intl = useIntl();
  const theme = useTheme();
  const history = useHistory();
  const authSignInStore = useSelector((state) => state.authSignInStore);
  const { isLoading, isSuccess, isError, error } = authSignInStore;
  const dispatch = useDispatch();

  const onSubmit = (values) => {
    dispatch(
      signIn({
        email: values.email,
        password: values.password,
        remember: values.remember ? true : false,
      })
    ).then((res) => {
      if (res) history.push('/');
    });
  };

  const validate = (values) => {
    const errors = {};
    if (!values.email)
      errors.email = intl.formatMessage({
        id: 'FORM.VALIDATION.THIS_FIELD_IS_REQUIRED',
      });
    if (
      values.email &&
      !/^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(
        values.email
      )
    )
      errors.email = intl.formatMessage({
        id: 'FORM.VALIDATION.INVALID_EMAIL_ADDRESS',
      });
    if (!values.password)
      errors.password = intl.formatMessage({
        id: 'FORM.VALIDATION.THIS_FIELD_IS_REQUIRED',
      });
    return errors;
  };

  const { form, handleSubmit, pristine, submitting, invalid } = useForm({
    onSubmit,
    validate,
  });
  const emailField = useField('email', form);
  const passwordField = useField('password', form);
  const rememberField = useField('remember', form);

  useEffect(() => {
    dispatch(signInInit());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Panel>
      <Logobox>
        <Logo src={IMAGE_LOGO} />
      </Logobox>

      <Textbox>
        {isError ? (
          <Text theme={theme.sign.text.error}>
            {intl.formatMessage({
              id: `SIGN_IN_PANEL.ERROR.${error}`,
              defaultMessage: error,
            })}
          </Text>
        ) : isSuccess ? (
          <Text theme={theme.sign.text.default}>
            {intl.formatMessage({
              id: `SIGN_IN_PANEL.SUCCESS`,
            })}
          </Text>
        ) : (
          <Text theme={theme.sign.text.default}>
            <Strong>
              {intl.formatMessage({
                id: 'SIGN_IN_PANEL.INFO.WELCOME',
              })}
            </Strong>
            ,{' '}
            {intl.formatMessage({
              id: 'SIGN_IN_PANEL.INFO.SIGN_IN_TO_CONTINUE',
            })}
            .
          </Text>
        )}
      </Textbox>

      {!isSuccess ? (
        <Form onSubmit={handleSubmit}>
          <Inputbox>
            <Input
              theme={theme.input.default}
              {...emailField.input}
              type='email'
              placeholder={intl.formatMessage({
                id: 'SIGN_IN_PANEL.INPUT.EMAIL_ADDRESS',
              })}
              autoComplete='on'
              invalid={emailField.meta.touched && emailField.meta.error}
              valid={
                emailField.input.value &&
                !(emailField.meta.touched && emailField.meta.error)
              }
            />
            <InputError theme={theme.inputError.default}>
              {emailField.meta.touched && emailField.meta.error}
            </InputError>
          </Inputbox>

          <Inputbox>
            <Input
              theme={theme.input.default}
              {...passwordField.input}
              type='password'
              placeholder={intl.formatMessage({
                id: 'SIGN_IN_PANEL.INPUT.PASSWORD',
              })}
              autoComplete='on'
              invalid={passwordField.meta.touched && passwordField.meta.error}
              valid={
                passwordField.input.value &&
                !(passwordField.meta.touched && passwordField.meta.error)
              }
            />
            <InputError theme={theme.inputError.default}>
              {passwordField.meta.touched && passwordField.meta.error}
            </InputError>
          </Inputbox>

          <Checkbox>
            <Checkmark
              id='remember-me'
              {...rememberField.input}
              type='checkbox'
            />
            <Check
              theme={theme.sign.check.default}
              checked={rememberField.input.value}
              htmlFor='remember-me'
            >
              Remember me
            </Check>
          </Checkbox>

          <SubmitButton
            theme={theme.button.default}
            disabled={submitting || pristine || invalid || isLoading}
          >
            {isLoading
              ? intl.formatMessage({
                  id: 'SIGN_IN_PANEL.SUBMIT_BUTTON.PLEASE_WAIT',
                })
              : intl.formatMessage({
                  id: 'SIGN_IN_PANEL.SUBMIT_BUTTON.SIGN_IN',
                })}
          </SubmitButton>

          <Linkbox theme={theme.sign.linkbox}>
            <Link to={`/forgot-password`}>
              {intl.formatMessage({
                id: 'SIGN_IN_PANEL.BUTTON.FORGOT_PASSWORD',
              })}
              ?
            </Link>
          </Linkbox>
        </Form>
      ) : (
        ''
      )}
    </Panel>
  );
};

SignInPanel.propTypes = {};

export default SignInPanel;
